import * as React from "react";
import { render } from "react-dom";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import log from "loglevel";
import NewScreen from "./features/screens/NewScreen";
import { FlowWindow } from "./globals";
import { FlowVellaLoader } from "./components/FlowVellaLoader";
import { BetaBadge } from "./components/BetaBadge";
// const NewScreen = React.lazy(() => import("./features/screens/NewScreen.tsx"));
import Modal from "./features/popups/Modal";
import FlowMachine from "./features/fsm/useMachine";
import Canvas from "./features/root/Canvas";
import SnackbarProvider from "./features/popups/SnackbarProvider";
import 'reactjs-popup/dist/index.css';

// AI Feature - initializes window.FlowVellaAI
import * as FlowVellaAI from "./features/ai";
export { FlowVellaAI };

declare let window: FlowWindow;
// At runtime, we need to adjust our definition of the screen size to ignore the php injected
// sidebar
export const sideNavWidth = 80;

// Need to checkout some issues with xstate? In comment these lines to see the inspector in a popup window.
// import { inspect } from "@xstate/inspect";
// inspect({
//   iframe: false,
// });
Sentry.init({
  dsn: `https://61370d37767d4af59cdb72b29b9d78c1@o161637.ingest.sentry.io/1227879`,
  integrations: [new Integrations.BrowserTracing()],
  beforeSend(event) {
    // Check if it is an exception, and if so, show the report dialog
    if (event.exception && window.flow) {
      Sentry.addBreadcrumb({
        category: `fromPhp`,
        message: "pre-flow",
        data: window.flow,
        level: Sentry.Severity.Info,
        type: `code`,
      });
    }
    return event;
  },
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

//@ts-ignore
log.info(`Version => `, __VERSION__);
if (process.env.NODE_ENV !== `production`) {
  // Enable LiveReload
  const script = document.createElement(`script`);
  script.src = `http://localhost:35729/livereload.js?snipver=1`;
  document.body.appendChild(script);
}

// Detect Safari browser on desktop (not mobile)
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafariBrowser = ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1 && ua.indexOf('chromium') === -1;
  const isMobile = /iphone|ipad|ipod|android/.test(ua);
  return isSafariBrowser && !isMobile;
};

const SafariWarning: React.FC<{}> = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'var(--background)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    zIndex: 10000,
  }}>
    <div style={{
      maxWidth: '600px',
      textAlign: 'center',
      color: 'var(--foreground)',
    }}>
      <h1 style={{ fontSize: '32px', marginBottom: '20px' }}>Safari Not Supported</h1>
      <p style={{ fontSize: '18px', lineHeight: '1.6', marginBottom: '20px' }}>
        The FlowVella Web Creator is not compatible with Safari due to browser limitations.
      </p>
      <p style={{ fontSize: '18px', lineHeight: '1.6', marginBottom: '30px' }}>
        Please use <strong>Chrome</strong> or <strong>Firefox</strong>
      </p>

      <div style={{ marginBottom: '30px' }}>
        <p style={{ fontSize: '18px', marginBottom: '15px' }}>Or download the FlowVella Mac app:</p>
        <a href="https://flowvella.com/download?from=safari-warning" target="_blank" rel="noopener noreferrer">
          <img src="/include/img/logos/flowvella-mac-2022.png" width={100} style={{ marginBottom: '15px' }} alt="FlowVella Mac App" />
        </a>
        <br />
        <a
          href="https://flowvella.com/download?from=safari-warning"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '12px 30px',
            backgroundColor: 'var(--foreground)',
            color: 'var(--background)',
            textDecoration: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Download Mac App
        </a>
        <p style={{ fontSize: '14px', marginTop: '15px', fontStyle: 'italic', opacity: 0.8 }}>
          Don't tell anyone, but it's better than our web version
        </p>
      </div>

      <a
        href="/user/myflows"
        style={{
          display: 'inline-block',
          padding: '10px 24px',
          color: 'var(--foreground)',
          textDecoration: 'underline',
          fontSize: '14px',
        }}
      >
        Back to My Flows
      </a>
    </div>
  </div>
);

export const App: React.FC<{}> = () => {
  // Block Safari users from using the creator
  if (isSafari()) {
    return <SafariWarning />;
  }

  return window.newFlow ? (
    <>
      <BetaBadge />
      <Modal trigger={<div></div>} isLoaded={true}>
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '100px',
          width: 'calc(100vw - 200px)',
          height: 'calc(100vh - 40px)',
          margin: 0,
          padding: '60px 40px 40px 40px',
          borderRadius: '40px',
          border: '2px solid var(--foreground)',
          backgroundColor: 'var(--background)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <NewScreen />
        </div>
      </Modal>
      <FlowMachine>
        <SnackbarProvider
          maxSnack={3}
          preventDuplicate
          anchorOrigin={{
            vertical: `top`,
            horizontal: `right`,
          }}
        >
          {/* flow var is injected by PHP from backend. a null value creates an empty canvas */}
          <Canvas />
        </SnackbarProvider>
      </FlowMachine>
    </>
  ) : (
      <>
      <BetaBadge />
        <FlowMachine>
          <SnackbarProvider
            maxSnack={3}
            preventDuplicate
            anchorOrigin={{
              vertical: `top`,
              horizontal: `right`,
            }}
          >
            <Canvas />
          </SnackbarProvider>
        </FlowMachine>
      </>
    );
}
// PHP injects the newFlow variable depending on which url is hit.
// The legacy editor mounts into #main_stage; other pages that load this
// bundle (e.g. /create, which only uses window.FlowVellaAI) won't have
// that container. Guard the mount so the bundle stays usable elsewhere.
const _mainStageEl = document.getElementById(`main_stage`);
if (_mainStageEl) {
  render(
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={
          <div
            style={{
              background: `white`,
              height: `100vh`,
              width: `calc(100vw - 80px)`,
              fontSize: `16px`,
            }}
            className="gentle-flex"
          >
            <p>
              You&#39;ve encountered an error. Please fill out the report so we
              can figure out what went wrong!
            </p>
          </div>
        }
        showDialog={true}
      >
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
    _mainStageEl
  );
}
